import React, { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import mobile_filter from "../../../assets/mobile_filter.svg";
import classes from "./termAndConditionsFilterPage.module.css";

import {
  selectTermsAndConditionsState,
  setTermsAndConditionsFilters,
} from "../../../store/termsAndConditions/termsAndConditionsFilterSlice";

import { useGetTermsAndConditionsMenuQuery } from "../../../services/termsAndConditions/termsAndConditionsMenuApiSlice";

const LEVELS = [
  { key: "mainCategory", label: "Main Category", parent: null },
  { key: "category", label: "Category", parent: "mainCategory" },
  { key: "subCategory", label: "Sub Category", parent: "category" },
];

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: { x: "-100%" },
  visible: {
    x: 0,
    transition: { type: "tween", duration: 0.35 },
  },
  exit: { x: "-100%", transition: { duration: 0.25 } },
};

export const TermAndConditionsFilterPage = () => {
  const dispatch = useDispatch();
  const reduxFilters = useSelector(selectTermsAndConditionsState);
  const { data: menuItems = [] } = useGetTermsAndConditionsMenuQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(() => ({
    mainCategory: searchParams.get("main_category_id") ? Number(searchParams.get("main_category_id")) : null,
    category: searchParams.get("category_id") ? Number(searchParams.get("category_id")) : null,
    subCategory: searchParams.get("sub_category_id") ? Number(searchParams.get("sub_category_id")) : null,
  }), [searchParams]);

  const [isOpen, setIsOpen] = useState(false);
  const [activeLevel, setActiveLevel] = useState("mainCategory");
  const navigate = useNavigate();

  /* ---------- LOCAL FILTER STATE ---------- */

  const [filters, setFilters] = useState({
    mainCategory: reduxFilters.main_category_id ?? null,
    category: reduxFilters.category_id ?? null,
    subCategory: reduxFilters.sub_category_id ?? null,
  });

  /* ---------- SYNC REDUX -> LOCAL ---------- */

  useEffect(() => {
    setFilters({
      mainCategory: reduxFilters.main_category_id ?? null,
      category: reduxFilters.category_id ?? null,
      subCategory: reduxFilters.sub_category_id ?? null,
    });
  }, [reduxFilters]);

  /* ---------- CATEGORY MAP ---------- */

  const categoryMap = useMemo(() => {
    const map = {};
    menuItems.forEach((item) => {
      const key = item.parent_id ?? null;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [menuItems]);

  /* ---------- OPTIONS ---------- */

  const getOptions = () => {
    const level = LEVELS.find((l) => l.key === activeLevel);
    if (!level.parent) return categoryMap[null] || [];
    const parentValue = filters[level.parent];
    if (!parentValue) return [];
    return categoryMap[parentValue] || [];
  };

  const options = getOptions();

  /* ---------- MESSAGE ---------- */

  const getMessage = () => {
    const level = LEVELS.find((l) => l.key === activeLevel);
    if (!level.parent) return null;
    const parentValue = filters[level.parent];
    if (!parentValue) {
      if (activeLevel === "category")
        return "Please select Main Category to see options";
      if (activeLevel === "subCategory")
        return "Please select Main Category and Category to see options";
    }
    return null;
  };

  const message = getMessage();

  /* ---------- UPDATE FILTER ---------- */

  const updateFilter = (value) => {
    setFilters((prev) => {
      const newValue = prev[activeLevel] === value ? null : value;
      const updated = { ...prev, [activeLevel]: newValue };

      if (activeLevel === "mainCategory") {
        updated.category = null;
        updated.subCategory = null;
      }
      if (activeLevel === "category") {
        updated.subCategory = null;
      }

      return updated;
    });
  };

  /* ---------- APPLY FILTERS ---------- */

  const handleApply = () => {
    const params = new URLSearchParams();
    const cleanedFilters = {};

    if (filters.mainCategory) {
      params.set("main_category_id", filters.mainCategory);
      cleanedFilters.main_category_id = filters.mainCategory;
    }
    if (filters.category) {
      params.set("category_id", filters.category);
      cleanedFilters.category_id = filters.category;
    }
    if (filters.subCategory) {
      params.set("sub_category_id", filters.subCategory);
      cleanedFilters.sub_category_id = filters.subCategory;
    }

    setSearchParams(params);
    dispatch(setTermsAndConditionsFilters(cleanedFilters));
    setIsOpen(false);
  };

  /* ---------- CLEAR FILTERS ---------- */

  const clearAll = () => {
    setFilters({ mainCategory: null, category: null, subCategory: null });
    setSearchParams({});
    dispatch(setTermsAndConditionsFilters({
      main_category_id: null,
      category_id: null,
      sub_category_id: null,
    }));
    setIsOpen(false);
  };

  const isFilterSelected =
    filters.mainCategory || filters.category || filters.subCategory;

  const urlActiveCount = [urlFilters.mainCategory, urlFilters.category, urlFilters.subCategory]
    .filter(Boolean).length;

  const handleAddTermsAndConditions = useCallback(() => {
    navigate("/terms_and_conditions/create");
  }, [navigate]);

  return (
    <div className={classes.box}>
      <h1 className={classes.box__content__title}>Terms & Conditions</h1>
      <div className={classes.box__filters}>
        <button
          className={`${classes.box__filters__tab} ${urlActiveCount > 0 ? classes.box__filters__tab_applied : ""}`}
          onClick={() => {
            setActiveLevel("mainCategory");
            setIsOpen(true);
          }}
        >
          <img src={mobile_filter} alt="Filter" width={20} height={20} />
          Filters
          {urlActiveCount > 0 && (
            <span className={classes.filterBadge}>{urlActiveCount}</span>
          )}
        </button>
        <button
          className={classes.box__create}
          onClick={handleAddTermsAndConditions}
        >
          Add Terms & Conditions
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.section
            className={classes.overlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.section
              className={classes.modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className={classes.modal__head}>
                <div className={classes.modal__titleWrap}>
                  <img src={mobile_filter} alt="filter" />
                  <h4>Filters</h4>
                </div>

                <button
                  className={classes.closeBtn}
                  onClick={() => setIsOpen(false)}
                >
                  ×
                </button>
              </div>

              <div className={classes.modal__content}>
                <div className={classes.leftMenu}>
                  {LEVELS.map((level) => (
                    <button
                      key={level.key}
                      className={`${classes.menuBtn} ${
                        activeLevel === level.key ? classes.menuActive : ""
                      } ${
                        urlFilters[level.key] != null ? classes.menuHasApplied : ""
                      }`}
                      onClick={() => setActiveLevel(level.key)}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>

                <div className={classes.separator} />

                <div className={classes.rightOptions}>
                  {message ? (
                    <p className={classes.placeholder}>{message}</p>
                  ) : (
                    options.map((opt) => {
                      const isApplied = urlFilters[activeLevel] === opt.id;
                      const isSelected = filters[activeLevel] === opt.id;
                      return (
                        <button
                          key={opt.id}
                          className={`${classes.option} ${
                            isApplied
                              ? classes.optionApplied
                              : isSelected
                              ? classes.optionSelected
                              : ""
                          }`}
                          onClick={() => updateFilter(opt.id)}
                        >
                          {opt.name}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className={classes.footer}>
                <button className={classes.clearBtn} onClick={clearAll}>
                  Clear All
                </button>

                <button className={classes.applyBtn} onClick={handleApply}>
                  Apply
                </button>
              </div>
            </motion.section>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};
